import {useState} from 'react';

import {ActionMenu, type SelectMenuOption} from 'shells/action-menu/action-menu';
import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {EditIcon, TrashRegularIcon} from 'shells/icon';

import {useRemoveSharedEmailAddress} from '../graphql/hooks/use-remove-shared-email-address';

import {EditSenderNameModal} from './edit-sender-name-modal';
import type {EmailAddressRow} from './settings';

interface Props {
    address: EmailAddressRow;
}

export function SharedAddressRowActionMenu(props: Props) {
    const [isConfirmingDelete, setIsConfirmingDelete] = useState<boolean>(false);
    const [editingAddress, setEditingAddress] = useState<{
        id: string;
        rawSenderName: string;
    } | null>(null);

    const {removeSharedEmailAddress} = useRemoveSharedEmailAddress();

    const handleRemove = () => {
        removeSharedEmailAddress({fromAddressId: props.address.id});
        setIsConfirmingDelete(false);
    };

    const options: SelectMenuOption[] = [
        {
            label: 'Edit sender name',
            handleSelect: () =>
                setEditingAddress({
                    id: props.address.id,
                    rawSenderName: props.address.rawSenderName,
                }),
            icon: <EditIcon size={18} wrapWithDiv={false} />,
        },
        {
            label: 'Remove',
            icon: <TrashRegularIcon size={18} wrapWithDiv={false} />,
            handleSelect: () => setIsConfirmingDelete(true),
            variant: 'destructive',
        },
    ];

    return (
        <>
            <ActionMenu options={options} size='tiny' hasHoverBackground={true} />
            {isConfirmingDelete && (
                <ConfirmationDialog
                    title='Are you sure you want to remove this shared address?'
                    message='This address will no longer be available for replying to emails using the Shared Inbox.'
                    onCancel={(e) => {
                        e?.stopPropagation();
                        setIsConfirmingDelete(false);
                    }}
                    onConfirm={handleRemove}
                />
            )}
            {editingAddress && (
                <EditSenderNameModal
                    isOpen={true}
                    onClose={() => setEditingAddress(null)}
                    addressId={editingAddress.id}
                    currentSenderName={editingAddress.rawSenderName}
                />
            )}
        </>
    );
}
